import './style.scss';

import React from 'react';
import { connect } from 'react-redux';
import store from 'shared/utils/localStore';
import Timer from './timer';
import View from 'components/ui/view';
import Text from 'components/ui/text';
import { LEAD_CAPTURE_CLOSED_AT_KEY } from 'components/login/actions';
import { loadPromos } from 'components/promo/actions';
import { firstPromoToApply } from 'shared/models/promo';
import { showAvailability } from 'components/availability/actions';
import { trackNow } from 'shared/models/analytic';
import { isEqual } from 'shared/models/address';
import config from 'sharedConfig';

const TIMER_LENGTH = 20 * 60 * 1000; // 20 minutes

let Banner = class Banner extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      message: this._message(props),
      timerExpired: false
    };
  }

  _message(props, state = {}) {
    if (this.props.checkout) return this._checkoutMessage(props);

    if (this.props.loadingVendor || this.props.promo.loading) return null;
    if (!this._hasAddress(props.address)) return 'noAddress';
    if (this._outsideDelivery(props.vendor)) return 'outsideDelivery';
    if (this._hasPromo(props.user, props.promo)) return 'hasPromo';
    if (this._newCustomer(props.leadCaptureClosedAt, props.user, state))
      return 'newCustomer';
    if (this._shippingOnly(props.address, props.vendor)) return 'shippingOnly';
    return 'onDemandAvailable';
  }

  _checkoutMessage(props) {
    if (!this._hasAddress(props.address)) return null;
    if (this._outsideDelivery(props.checkoutVendor)) return 'outsideDelivery';
    if (this._shippingOnly(props.address, props.checkoutVendor))
      return 'shippingOnly';
    return 'onDemandAvailable';
  }

  _hasAddress(address) {
    return address && address.lat && address.lng;
  }

  _outsideDelivery(vendor) {
    if (!vendor) return;
    return !vendor.inside_delivery_zone && !vendor.shipping;
  }

  _hasPromo(user, promo) {
    if (!user) return;
    return !!this.availableCredit(promo);
  }

  _newCustomer(leadCaptureClosedAt, user, state = {}) {
    if (state.timerExpired) return;
    const registeredAt = this.userRegisteredAt(leadCaptureClosedAt, user);
    if (!registeredAt) return;
    const msAgo = new Date() - new Date(registeredAt);
    return msAgo < TIMER_LENGTH;
  }

  _shippingOnly(address, vendor) {
    if (!this._hasAddress(address)) return;
    if (!vendor) return;
    return !!vendor.shipping;
  }

  componentDidMount() {
    this._checkPromoCodes(this.props);

    const message = this.state.message;
    if (message) {
      if (this.props.onMessageChange) this.props.onMessageChange(message);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.user && this.props.user !== prevProps.user) {
      this._checkPromoCodes(this.props);
    }

    if (this._hasChanged(prevProps, prevState)) {
      this.setMessage(this._message(this.props, this.state));
    }

    if (this.state.message && this.state.message !== prevState.message) {
      this._track(this.state.message, this.props);
    }
  }

  setMessage(message) {
    this.setState({ message });
    if (this.props.onMessageChange) this.props.onMessageChange(message);
  }

  _checkPromoCodes(props) {
    const codes = props.promo.codes || [];
    if (props.user && codes.length === 0 && !props.checkout) props.loadPromos();
  }

  _track(message, props) {
    const map = {
      noAddress: 'no address',
      outsideDelivery: 'outside delivery',
      hasPromo: 'promo available',
      newCustomer: 'new user',
      shippingOnly: 'shipping',
      onDemandAvailable: 'on-demand'
    };

    trackNow('Site Banner Viewed', {
      banner: map[message],
      checkout: props.checkout
    });
  }

  _hasChanged(prevProps, prevState) {
    return (
      !isEqual(prevProps.address, this.props.address) ||
      prevProps.vendor !== this.props.vendor ||
      prevProps.checkoutVendor !== this.props.checkoutVendor ||
      prevProps.user !== this.props.user ||
      prevProps.promo !== this.props.promo ||
      prevProps.leadCaptureClosedAt !== this.props.leadCaptureClosedAt ||
      prevState.timerExpired !== this.state.timerExpired
    );
  }

  userRegisteredAt(leadCaptureClosedAt, user) {
    if (!leadCaptureClosedAt && typeof window !== 'undefined') {
      leadCaptureClosedAt = store.get(LEAD_CAPTURE_CLOSED_AT_KEY);
    }
    const userCreatedAt = user && +new Date(user.createdAt);
    if (leadCaptureClosedAt && userCreatedAt) {
      return leadCaptureClosedAt < userCreatedAt
        ? leadCaptureClosedAt
        : userCreatedAt;
    } else if (leadCaptureClosedAt) {
      return leadCaptureClosedAt;
    } else if (userCreatedAt) {
      return userCreatedAt;
    }
  }

  availableCredit(promo = {}) {
    const hasValidType = ({ value_type }) =>
      ['credit', '$', '%', 'sku'].indexOf(value_type) > -1;

    const { credit, codes } = promo;
    if (credit && credit.value && hasValidType(credit)) return credit;

    const code = firstPromoToApply(codes);
    if (code && code.value && hasValidType(code)) return code;
  }

  render() {
    const message = this.state.message;
    if (!message) return null;

    const className =
      'row middle-xs center-xs site-banner' +
      (message === 'outsideDelivery' ? ' site-banner--danger' : '');

    let expiresAt;
    if (message === 'newCustomer') {
      expiresAt = new Date(
        this.userRegisteredAt(this.props.leadCaptureClosedAt, this.props.user) +
          TIMER_LENGTH
      );
    }

    let credit;
    if (message === 'hasPromo') {
      credit = this.availableCredit(this.props.promo);
    }

    return (
      <div className={className}>
        <div className="col-xs-12">
          <View padding={{ sides: 'sm', top: 'xs', bottom: 'xs' }}>
            {message === 'noAddress' && (
              <div
                className="clickable"
                onClick={() => this.props.showAvailability()}
              >
                <Text title size="xs">
                  Set your delivery address to see local pricing and delivery
                  options.
                </Text>
              </div>
            )}

            {message === 'outsideDelivery' && (
              <Text title size="xs" weight="med">
                Uh oh, looks like we don{"'"}t deliver to that address.{' '}
                {!this.props.checkout && (
                  <span>
                    <a
                      className="link-text"
                      onClick={() => this.props.showAvailability()}
                    >
                      Click here
                    </a>{' '}
                    to enter a new one.
                  </span>
                )}
              </Text>
            )}

            {message === 'hasPromo' &&
              credit.value_type === 'credit' && (
                <Text title size="xs" weight="light">
                  You have{' '}
                  <Text weight="med" tag="span">
                    ${credit.balance} in available credit.{' '}
                  </Text>
                  Checkout now to redeem.
                </Text>
              )}

            {message === 'hasPromo' &&
              credit.value_type === '$' && (
                <Text title size="xs" weight="light">
                  You have{' '}
                  <Text weight="med" tag="span">
                    ${credit.value} in available credit.{' '}
                  </Text>
                  Checkout now to redeem.
                </Text>
              )}

            {message === 'hasPromo' &&
              credit.value_type === '%' && (
                <Text title size="xs" weight="light">
                  You have{' '}
                  <Text weight="med" tag="span">
                    {credit.value * 100}% off your next order.{' '}
                  </Text>
                  Checkout now to redeem.
                </Text>
              )}

            {message === 'hasPromo' &&
              credit.value_type === 'sku' && (
                <Text title size="xs" weight="light">
                  You have a promo code:{' '}
                  <Text weight="med" tag="span">
                    {credit.desc}.{' '}
                  </Text>
                  Checkout now to redeem.
                </Text>
              )}

            {message === 'newCustomer' && (
              <Text title size="xs" weight="med">
                Checkout in{' '}
                <div className="site-banner__timer">
                  <Timer
                    expiresAt={expiresAt}
                    handleExpired={() => this.setState({ timerExpired: true })}
                  />
                </div>{' '}
                for $10 off your first order. Enter code FIRST10 at Checkout.
                Restrictions Apply
              </Text>
            )}

            {message === 'shippingOnly' && (
              <Text title size="xs" weight="light">
                Shipping is available in your area.{' '}
                <Text weight="med" tag="span">
                  Free ground shipping{' '}
                </Text>
                on orders over ${config.shipping.freeThreshold}
              </Text>
            )}

            {message === 'onDemandAvailable' && (
              <Text title size="xs" weight="light">
                30-min Delivery is available in your city.{' '}
                <Text weight="med" tag="span">
                  No delivery fees &amp; no order minimums.{' '}
                </Text>
                Ever.
              </Text>
            )}
          </View>
        </div>
      </div>
    );
  }
};

Banner = connect(
  state => ({
    address: state.address,
    vendor: state.address.vendor,
    loadingVendor: state.address.loadingVendor,
    checkoutVendor: state.checkout.vendor,
    promo: state.promo,
    user: state.login.user,
    leadCaptureClosedAt: state.login.leadCaptureClosedAt
  }),
  { loadPromos, showAvailability }
)(Banner);

export default Banner;



// WEBPACK FOOTER //
// ./shared/components/banner/index.js